/**
 * PATH: packages/job-hub/src/jobs/tabs/JobQaExecution.tsx
 * FILE_ID: JOBHUB-JOBS-QA-EXECUTION-001
 * PURPOSE: Run-mode body for one area, filtered by the shared checklist rail.
 */
import { useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Circle, MinusCircle, XCircle } from 'lucide-react'
import {
  QaOwnerVerification,
  QaStatusFilterChips,
  QaStatusMenu,
} from '@quotech/ui'
import { cn } from '@quotech/tenant-shared/lib/utils'
import {
  areaItems,
  areaProgress,
  checklistGroups,
  createQaApi,
  groupEvidenceByItem,
  type QaAreaExecution,
  type QaItemStatus,
} from '../qa/qaApi'
import { itpSectionTitle } from '../qa/itpDocument'
import { QA_STATUS_OPTIONS, matchesRunFilter, runFilterCounts } from '../qa/qaStatusOptions'
import { ChecklistItemRow, ReadingCapture } from './job-qa/ChecklistItemRow'
import { QaRunNextBar, type AreaPick } from './job-qa/QaRunNextBar'
import type { RunArea } from './job-qa/qaRunNext'
import { QaMergedAreas } from './job-qa/useQaEvidenceUrls'
import { QaChecklistSectionHeader } from './job-qa/QaChecklistSectionHeader'
import { useStickyOffset } from './job-qa/useStickyOffset'

export { ChecklistItemRow, ReadingCapture }

export function StatusIcon({ status }: { status: QaItemStatus }) {
  if (status === 'done' || status === 'pass') return <CheckCircle2 className="h-5 w-5 text-status-good" aria-hidden />
  if (status === 'failed') return <XCircle className="h-5 w-5 text-status-bad" aria-hidden />
  if (status === 'not_applicable') return <MinusCircle className="h-5 w-5 text-muted-foreground" aria-hidden />
  if (status === 'further_action') return <AlertTriangle className="h-5 w-5 text-status-warn" aria-hidden />
  return <Circle className="h-5 w-5 text-muted-foreground" aria-hidden />
}

export function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
      <div
        className={cn('h-full rounded-full transition-all', pct >= 100 ? 'bg-status-good' : 'bg-primary')}
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  )
}

export function RunScopePickers({
  areaName,
  checklistName,
  areas,
  checklists,
  currentAreaId,
  currentChecklistId,
  onSelectArea,
  onSelectChecklist,
  hideChecklist = false,
}: {
  areaName: string
  checklistName: string | null
  areas: { id: string; name: string; group?: string }[]
  checklists: { id: string; name: string }[]
  currentAreaId: string
  currentChecklistId: string | null
  onSelectArea: (areaId: string) => void
  onSelectChecklist: (checklistId: string) => void
  hideChecklist?: boolean
}) {
  const [openPicker, setOpenPicker] = useState<'areas' | 'checklists' | null>(null)
  const toggle = (which: 'areas' | 'checklists') => {
    setOpenPicker((current) => (current === which ? null : which))
  }
  const rows = openPicker === 'areas' ? areas : openPicker === 'checklists' ? checklists : []
  return (
    <div className="mb-2 min-w-0">
      <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          aria-label="Choose area"
          className="min-h-8 max-w-full truncate rounded-md border border-border bg-card px-2.5 py-1 text-left text-sm"
          onClick={() => toggle('areas')}
        >
          {areas.find((row) => row.id === currentAreaId)?.group
            ? `Areas > ${areas.find((row) => row.id === currentAreaId)?.group} > ${areaName}`
            : `Areas > ${areaName}`}
        </button>
        {hideChecklist ? null : <button
          type="button"
          aria-label="Choose checklist"
          className="min-h-8 max-w-full truncate rounded-md border border-border bg-card px-2.5 py-1 text-left text-sm"
          onClick={() => toggle('checklists')}
        >
          {checklistName ? `Checklist > ${checklistName}` : 'Checklist'}
        </button>}
      </div>
      {openPicker ? (
        <div className="mt-1 max-h-[45vh] overflow-auto rounded-md border border-border">
          {rows.map((row) => {
            const current = openPicker === 'areas'
              ? row.id === currentAreaId
              : row.id === currentChecklistId
            return (
              <button
                key={row.id}
                type="button"
                className={cn(
                  'flex min-h-11 w-full items-center truncate px-3 text-left text-sm',
                  current && 'bg-primary/10',
                  openPicker === 'areas' && 'group' in row && row.group && 'pl-6',
                )}
                onClick={() => {
                  if (openPicker === 'areas') onSelectArea(row.id)
                  else onSelectChecklist(row.id)
                  setOpenPicker(null)
                }}
              >
                {row.name}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export function AreaExecutionPanel(props: Parameters<typeof AreaExecutionBody>[0]) {
  return (
    <QaMergedAreas api={props.api} areas={[props.area]} enabled>
      {([area]) => <AreaExecutionBody {...props} area={area ?? props.area} />}
    </QaMergedAreas>
  )
}

function AreaExecutionBody({
  area,
  checklistId = null,
  areas,
  checklists,
  onSelectArea,
  onSelectChecklist,
  jobId,
  api,
  onMutated,
  onSetStatus,
  onSetAreaStatus,
  statusPending,
  onOpenItp,
  onWriteError,
  itemSelection,
  checklistSelection,
  onRenameChecklist,
  startRenameId,
  onRenameStarted,
  hideTitleRow = false,
  scopeAreas,
  onOpenAreaPicker,
  areaPick = null,
  onShownArea,
}: {
  area: QaAreaExecution
  checklistId?: string | null
  areas?: { id: string; name: string; group?: string }[]
  checklists?: { id: string; name: string }[]
  onSelectArea?: (areaId: string) => void
  onSelectChecklist?: (checklistId: string) => void
  jobId: number
  api: ReturnType<typeof createQaApi>
  onMutated: () => void
  onSetStatus: (itemId: string, status: QaItemStatus) => void
  onSetAreaStatus: (areaId: string, status: QaItemStatus) => void
  statusPending: boolean
  onOpenItp?: (checklistIds: string[], itemIds?: string[]) => void
  onWriteError?: (message: string) => void
  itemSelection: {
    isSelected: (id: string, checklistId?: string, areaId?: string) => boolean
    onToggle: (id: string, checklistId?: string, areaId?: string) => void
  }
  checklistSelection?: { isSelected: (id: string) => boolean; onToggle: (id: string) => void }
  onRenameChecklist?: (checklistId: string, title: string) => void
  startRenameId?: string | null
  onRenameStarted?: () => void
  hideTitleRow?: boolean
  scopeAreas?: RunArea[]
  onOpenAreaPicker?: () => void
  areaPick?: AreaPick | null
  onShownArea?: (id: string) => void
}) {
  const scoped = useMemo(
    () => ({
      checklists: checklistId
        ? area.checklists.filter((checklist) => checklist.id === checklistId)
        : area.checklists,
    }),
    [area.checklists, checklistId],
  )
  const items = useMemo(() => areaItems(scoped), [scoped])
  const progress = areaProgress({
    checklists: scoped.checklists.map((checklist) => ({
      ...checklist,
      items: checklist.items,
    })),
  })
  const evidenceByItem = useMemo(() => groupEvidenceByItem(area.evidence), [area.evidence])
  const [statusFilter, setStatusFilter] = useState<QaItemStatus | null>(null)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const headerOffset = useStickyOffset()
  const statusCounts = useMemo(() => runFilterCounts(items), [items])
  const visibleItems = items.filter((item) => matchesRunFilter(item.status, statusFilter))
  const visibleGroups = useMemo(
    () => checklistGroups(scoped, visibleItems),
    [scoped, visibleItems],
  )
  const heading = checklistId
    ? itpSectionTitle(area.area_name, scoped.checklists[0]?.template_name?.trim() || 'Checklist')
    : area.area_name

  return (
    <div
      className="rounded-xl border border-border bg-card"
      style={{ ['--qa-section-top' as string]: `${headerOffset.height}px` }}
    >
      <div ref={headerOffset.ref} className="z-20 border-b border-border bg-card p-4 md:sticky md:top-0">
        {areas && checklists && onSelectArea && onSelectChecklist ? (
          <RunScopePickers
            areaName={area.area_name}
            checklistName={checklists.find((row) => row.id === checklistId)?.name ?? null}
            areas={areas}
            checklists={checklists}
            currentAreaId={area.id}
            currentChecklistId={checklistId}
            onSelectArea={onSelectArea}
            onSelectChecklist={onSelectChecklist}
          />
        ) : null}
        {hideTitleRow ? null : (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-foreground truncate" title={heading}>
              {heading}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <span data-testid="qa-run-progress" className="text-[11px] text-muted-foreground tabular-nums">
                {progress.done}/{progress.total} · {progress.percent}%
              </span>
              <QaStatusMenu
                status={area.work_status ?? 'not_started'}
                onChange={(status) => onSetAreaStatus(area.id, status)}
                disabled={area.status === 'locked'}
                targetLabel={area.area_name}
              />
            </div>
          </div>
        )}
        <div className="mt-2">
          <ProgressBar pct={progress.percent} />
        </div>
        <QaOwnerVerification
          className="mt-2"
          targetLabel={area.area_name}
          owner_verified={area.owner_verified}
          owner_verified_at={area.owner_verified_at}
          owner_verified_by_name={area.owner_verified_by_name}
          can_verify_completion={area.can_verify_completion}
          onVerify={async (verified) => {
            await api.verifyAreaCompletion(area.id, verified)
            onMutated()
          }}
        />
        {items.length > 0 && (
          <QaStatusFilterChips
            counts={statusCounts}
            value={statusFilter}
            onChange={setStatusFilter}
            options={QA_STATUS_OPTIONS}
            total={items.length}
            className="mt-3"
          />
        )}
      </div>

      {items.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
          No checklist items in this area.
          <button
            type="button"
            onClick={() => void api.addItem(area.id, {}, null, null, checklistId).then(onMutated)}
            className="mt-3 block w-full rounded-lg border border-dashed border-border px-3 py-2 text-xs text-primary"
          >
            Add the first check
          </button>
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
          No items with this status.
          <button
            type="button"
            onClick={() => setStatusFilter(null)}
            className="ml-1 text-primary hover:underline"
          >
            Clear filter
          </button>
        </div>
      ) : (
        visibleGroups.map((group) => (
          <section key={group.checklist.id}>
            <QaChecklistSectionHeader
              title={group.name}
              done={group.done}
              total={group.total}
              expanded={!collapsed[group.checklist.id]}
              onToggle={() => setCollapsed((state) => ({
                ...state,
                [group.checklist.id]: !state[group.checklist.id],
              }))}
              selected={checklistSelection?.isSelected(group.checklist.id)}
              onToggleSelected={checklistSelection
                ? () => checklistSelection.onToggle(group.checklist.id)
                : undefined}
              onRename={onRenameChecklist
                ? (title) => onRenameChecklist(group.checklist.id, title)
                : undefined}
              startRename={startRenameId === group.checklist.id}
              onRenameStarted={onRenameStarted}
              onOpenItp={onOpenItp ? () => onOpenItp([group.checklist.id]) : undefined}
              stickyClass="top-0 md:top-[var(--qa-section-top,0px)]"
            />
            {!collapsed[group.checklist.id] && (
              <ul className="divide-y divide-border">
                {group.items.map((item) => (
                  <ChecklistItemRow
                    key={item.id}
                    item={item}
                    evidence={evidenceByItem[item.id] ?? []}
                    jobId={jobId}
                    areaId={area.id}
                    api={api}
                    onMutated={onMutated}
                    onSetStatus={onSetStatus}
                    statusPending={statusPending}
                    selection={{
                      selected: itemSelection.isSelected(item.id, group.checklist.id),
                      onToggle: () => itemSelection.onToggle(item.id, group.checklist.id),
                    }}
                    onOpenItp={onOpenItp ? () => onOpenItp([group.checklist.id], [item.id]) : undefined}
                    onWriteError={onWriteError}
                    signoff={{ holdPoints: area.hold_points ?? [], canSign: Boolean(area.can_sign_hold_points) }}
                    checksLocked={area.status === 'locked'}
                  />
                ))}
              </ul>
            )}
          </section>
        ))
      )}
      {onSelectArea && onSelectChecklist ? (
        <QaRunNextBar
          scopeAreas={scopeAreas ?? areas ?? []}
          viewedAreaId={area.id}
          viewedChecklistId={checklistId}
          currentChecklists={checklistGroups(area).map((group) => ({
            id: group.checklist.id,
            name: group.name,
          }))}
          api={api}
          onOpenAreaPicker={onOpenAreaPicker}
          areaPick={areaPick}
          onShownArea={onShownArea}
          onGo={(areaId, nextChecklistId) => {
            // Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneSheetHost.tsx (onJump)
            onSelectArea(areaId)
            if (nextChecklistId) onSelectChecklist(nextChecklistId)
          }}
        />
      ) : null}
    </div>
  )
}
